import flattenDeep from "lodash/flattenDeep";
import flatten from "lodash/flatten";
import { getSumOfStreams } from "../selectors/calculation-selectors.js";
import { getShortValueWithPrefixedUnit } from "utils/formatters";
import { COLORS } from "colorTheme/currentTheme";
import { shadowMetricEnum, summaryFunctionMappingList } from "constants/consts";

export const AGGREGATED = "AGGREGATED";

const getMetricStream = (metricsStreams, metric) =>
  metricsStreams.find(streams =>
    streams.metric.map(x => x.name).includes(metric)
  );

export const prepareAllMetrics = (metricNames, metricStreams) => {
  return metricNames.slice(0, metricStreams.length).map(metricName => {
    const metricStream = metricStreams.find(
      m => m.metric[0] && m.metric[0].name === metricName
    );
    return (
      metricStream && {
        displayName: metricStream.metric[0].name,
        streams: metricStream.metric,
      }
    );
  });
};

export const alignStreamsToStartFromTheSameTime = streams => {
  const listOfStreams = JSON.parse(JSON.stringify(streams));

  let uqniueTimes = [];
  listOfStreams.forEach(x => {
    if (x.stream.length > 0) {
      x.stream.forEach(point => {
        uqniueTimes.push(point.time);
      });
    }
  });

  uqniueTimes = [...new Set(uqniueTimes)];
  for (let index = 0; index < listOfStreams.length; index++) {
    if (listOfStreams[index].stream.length > 0) {
      uqniueTimes.forEach(time => {
        if (!listOfStreams[index].stream.find(point => point.time === time)) {
          listOfStreams[index].stream.push({ value: 0, time: time });
        }
      });

      listOfStreams[index].stream = listOfStreams[index].stream.sort(
        (a, b) => (a.time > b.time ? 1 : -1)
      );
    }
  }
  return listOfStreams;
};

const mergeFilters = (filter, metric) => {
  const newFilter = JSON.parse(JSON.stringify(filter));

  metric.tags.forEach(x => {
    newFilter.tagsFilter.push({ name: x.name, values: x.values });
  });
  return newFilter;
};

export const mergeParams = (filter, metric) => {
  let param = {
    metrics: metric.metric,
    filter: mergeFilters(filter, metric),
  };

  if (!metric.summaryFunction || metric.summaryFunction === "average") {
    param["summaryFunction"] = "mean";
  } else {
    param["summaryFunction"] = metric.summaryFunction;
  }

  if (metric.groupFunction || metric.groupFunction === "average") {
    param["groupByFunction"] = "mean";
  } else {
    param["groupByFunction"] = metric.groupFunction;
  }

  if (metric.groupBy && metric.groupBy.length > 0) {
    param["groupBy"] = metric.groupBy;
  } else {
    param["groupBy"] = ["*"];
  }

  return param;
};

export const prepareCuratedMetrics = (
  curatedMetrics,
  curatedMetricsStreams
) => {
  if (curatedMetricsStreams.length === 0) return [];
  return curatedMetrics.map(curatedMetric => {
    const streams = curatedMetric.metrics.map(metric => {
      const metricStream = getMetricStream(curatedMetricsStreams, metric);
      return metricStream && metricStream.metric;
    });

    return {
      displayName: curatedMetric.displayName,
      streams: flattenDeep(streams),
    };
  });
};

const isComposite = metric => metric.match(/\(/);

export const formatMetricInQueue = (metric, tags) => {
  if (isComposite(metric)) {
    let filter = [];
    let group_by = [];
    let compositeMetric = metric;

    tags.map(tag => {
      if (tag.values) {
        filter = [
          ...filter,
          ...flatten(tag.values.map(value => `"${tag.name}":"${value}"`)),
        ];
      }

      if (tag.grouped) {
        group_by = [...group_by, tag.name];
      }
    });

    if (compositeMetric.match(/group_by/)) {
      // regex matching 'group_by("%",'
      const regex = /group_by\s*\(\s*"%"\s*,/;
      compositeMetric = compositeMetric.replace(
        regex,
        `group_by("${group_by.join(",")}",`
      );
    }

    return compositeMetric.split(/"%"/).join(`{${flatten(filter)}}`);
  }
  return metric;
};

export const getSummaryFunctionMapped = (
  summaryFunction,
  mappingList = summaryFunctionMappingList
) =>
  mappingList.find(el => el.key === summaryFunction)?.summary ||
  summaryFunction;

export const formatTagsInQueue = (metric, tags) => {
  if (isComposite(metric)) {
    return [];
  }
  return tags;
};

export const formatMetricAndTags = metric => {
  const reducedTags = reduceToMinimumTagSet(metric.name, metric.tags);
  const tags = formatTagsInQueue(metric.name, reducedTags);
  const name = formatMetricInQueue(metric.name, reducedTags);
  return [name, tags];
};

export const getMetricUniqueTagNames = metrics => {
  if (metrics.length === 1) {
    const metricName = metrics[0].name;
    const foundElement =
      UNIQUE_TAG_FOR_METRICS_LIST.find(x => x.metricName === metricName) || {};
    return foundElement.tag ? [foundElement.tag] : [];
  }

  let tagNames = metrics.map(metric => metric.tags.map(t => t.name));
  tagNames = [...new Set(flattenDeep(tagNames))];
  let uniqueTags = [];

  tagNames.forEach(tag => {
    let tagValues = [];
    metrics.forEach(metric => {
      const metricTag = metric.tags.find(t => t.name === tag);
      if (metricTag) {
        tagValues = [...tagValues, metricTag.values.join(":")];
      }
    });

    if (
      tagValues.length === [...new Set(tagValues)].length &&
      tagValues.length === metrics.length
    ) {
      uniqueTags = [...uniqueTags, tag];
    }
  });

  return uniqueTags;
};

const getMetricUniqValue = (metricTags, tags) => {
  if (tags.length === 0 || metricTags.length === 0) return;
  const filteredTags = metricTags
    .filter(tag => tags.includes(tag.name))
    .map(t => t.values);

  return flattenDeep(filteredTags).join(":");
};

export const getKindName = (
  metricName,
  kindNames,
  defaultName,
  metricTags,
  uniqTags,
  uniqueTagName
) => {
  const kind = kindNames[metricName] || defaultName;
  const uniq = uniqueTagName || getMetricUniqValue(metricTags, uniqTags);

  if (kind) {
    return kind + (uniq ? ` (${uniq})` : "");
  } else if (uniq) {
    return uniq;
  } else {
    return metricName;
  }
};

export const getMetricColor = stream => {
  if (
    stream.id === shadowMetricEnum.POSITIVE ||
    stream.id === shadowMetricEnum.NEGATIVE
  ) {
    return shadowMetricEnum.SHADOW_COLOR;
  }
  return COLORS.SAN_MARIO_BLUE;
};

const MINIMUM_HOST_TAG_LIST = [
  "host_hostname_alias",
  "apm_hostname_alias",
  "name",
  "dyno",
  "hostname",
  "instanceid",
];

export const CONTAINERS_METRICS = {
  CPU: `docker.cpu.cpu_usage.usage_percent`,
  Memory: `docker.memory.usage.usage_percent`,
  NetworkIO: `zero_fill(sum(rate(derive(scale(s("docker.network.*bytes", "%", { period:"60"}),{ factor: "8/1000" }),{ "detect_reset": "true" }))))`,
  MemoryRSS: `docker.memory.statistics.rss`,
};

const UNIQUE_TAG_FOR_METRICS_LIST = [
  { metricName: "system.net.bytes.rx", tag: "interface" },
  { metricName: "system.net.bytes.tx", tag: "interface" },
  { metricName: "system.net.interface.bytes.rx", tag: "interface" },
  { metricName: "system.net.interface.bytes.tx", tag: "interface" },
  { metricName: "system.net.errors.rx", tag: "interface" },
  { metricName: "system.net.errors.tx", tag: "interface" },
  { metricName: "system.net.interface.errors.rx", tag: "interface" },
  { metricName: "system.net.interface.errors.tx", tag: "interface" },
  { metricName: "system.net.drop.rx", tag: "interface" },
  { metricName: "system.net.drop.tx", tag: "interface" },
  { metricName: "system.net.interface.drop.rx", tag: "interface" },
  { metricName: "system.net.interface.drop.tx", tag: "interface" },
  { metricName: "system.disk.percent.used", tag: "mount_point" },
  { metricName: "system.io.io_time", tag: "mount_point" },
  { metricName: "system.io.bytes.write", tag: "mount_point" },
  { metricName: "system.io.bytes.read", tag: "mount_point" },
  { metricName: "system.process.cpu", tag: "process_name" },
  { metricName: "system.process.memory", tag: "process_name" },
  { metricName: CONTAINERS_METRICS["CPU"], tag: "docker_id" },
  { metricName: CONTAINERS_METRICS["Memory"], tag: "docker_id" },
  { metricName: CONTAINERS_METRICS["NetworkIO"], tag: "docker_id" },
  { metricName: CONTAINERS_METRICS["MemoryRSS"], tag: "docker_id" },
];

export const reduceToMinimumTagSet = (metricName, tags) => {
  if (!tags) {
    return [];
  }
  const atHost = getAtHostTag(tags);
  const atHostTag = atHost ? [{ name: "@host", values: [atHost] }] : [];
  const foundElement =
    UNIQUE_TAG_FOR_METRICS_LIST.find(x => x.metricName === metricName) || {};
  const uniqueTags = tags.filter(tag => tag.name === foundElement.tag);
  const allTags = [...uniqueTags, ...atHostTag];

  return allTags;
};

export function getAtHostTag(tags) {
  if (!tags) {
    return [];
  }
  let atHostTag = null;
  for (let hostTag of MINIMUM_HOST_TAG_LIST) {
    atHostTag = tags.filter(tag => {
      return tag.name === hostTag;
    });
    if (atHostTag.length !== 0) break;
  }
  return atHostTag && atHostTag.length !== 0 && getValueFromTag(atHostTag[0]);
}

function getValueFromTag(tag) {
  return tag.values ? tag.values[0] : tag.value;
}

export function formatAtHostTag(tag) {
  return [{ name: "@host", values: [tag] }];
}
export const addAveragesToMetrics = metrics => {
  metrics.forEach(metric => {
    const streams = metric && metric.streams;
    Object.assign(metric, calculateAvg(streams));
  });
};

export const calculateAvg = streams => {
  const streamsNames = streams.map(s => s.name);

  const sumOfStreams = streams && getSumOfStreams(streams.map(m => m.stream));
  const sumOfStreamsLength = sumOfStreams && sumOfStreams.length;
  const uniqueMetricsInStream = streamsNames.length
    ? streamsNames.length / [...new Set(streamsNames)].length
    : 1;

  const averageValue =
    sumOfStreamsLength > 0 &&
    sumOfStreams.reduce((a, b) => {
      return a + b.value;
    }, 0) /
      (sumOfStreamsLength * uniqueMetricsInStream);

  const shortAvgValue = getShortValueWithPrefixedUnit({
    number: parseFloat(averageValue),
    precision: 2,
  });
  const avg = {
    averageValue: averageValue || "-",
    shortAverageValue: shortAvgValue || "-",
  };
  return avg;
};
